import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps the free personalisation add-on in step with the cart: one add-on line while any
 * item carries personalisation properties, none otherwise. The add-on product sits in its
 * own shipping profile, which is how checkout turns off Cash on Delivery for personalised orders.
 *
 * @typedef {object} PersonalisationAddonConfig
 * @property {number} variantId - The add-on product's variant ID.
 * @property {string[]} propertyNames - Line item properties that mark an item as personalised.
 * @property {boolean} needsSync - Whether the cart rendered by Liquid was already out of sync.
 *
 * @typedef {object} CartLine
 * @property {string} key
 * @property {number} variant_id
 * @property {number} quantity
 * @property {Record<string, string> | null} properties
 */

const SOURCE = 'personalisation-addon';

class PersonalisationAddonSync {
  /** @type {PersonalisationAddonConfig} */
  #config;

  /** @type {Promise<void> | null} */
  #running = null;

  #queued = false;

  /** @param {PersonalisationAddonConfig} config */
  constructor(config) {
    this.#config = config;
  }

  start() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener('input', this.#handleInput);

    for (const input of document.querySelectorAll('input[name^="properties["]')) {
      if (input instanceof HTMLInputElement) this.#updateFormState(input);
    }

    if (this.#config.needsSync) this.#schedule();
  }

  /** @param {Event & { detail?: { data?: { source?: string } } }} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === SOURCE) return;

    this.#schedule();
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (event.target instanceof HTMLInputElement) this.#updateFormState(event.target);
  };

  /**
   * Flags a product form while it holds personalisation details, so buttons that skip the
   * cart (and therefore the add-on) can be hidden with CSS.
   * @param {HTMLInputElement} input
   */
  #updateFormState(input) {
    const { form } = input;
    if (!form || !this.#isPersonalisationInput(input)) return;

    const isPersonalised = Array.from(form.elements).some(
      (element) =>
        element instanceof HTMLInputElement && this.#isPersonalisationInput(element) && element.value.trim() !== ''
    );

    form.toggleAttribute('data-personalised', isPersonalised);
  }

  /** @param {HTMLInputElement} input */
  #isPersonalisationInput(input) {
    return this.#config.propertyNames.some((name) => input.name === `properties[${name}]`);
  }

  /** Runs one sync at a time; a request made mid-sync runs once more afterwards. */
  #schedule() {
    if (this.#running) {
      this.#queued = true;
      return;
    }

    this.#running = this.#sync()
      .catch((error) => console.error(error))
      .finally(() => {
        this.#running = null;

        if (this.#queued) {
          this.#queued = false;
          this.#schedule();
        }
      });
  }

  async #sync() {
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) return;

    const cart = await cartResponse.json();
    const updates = this.#getUpdates(cart.items);
    if (!updates) return;

    const sectionIds = new Set();
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.add(cartItems.dataset.sectionId);
      }
    }

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    const updateResponse = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body }));
    const updatedCart = await updateResponse.json();
    if (!updateResponse.ok || updatedCart.status) return;

    document.dispatchEvent(
      new CartUpdateEvent(updatedCart, SOURCE, {
        source: SOURCE,
        itemCount: updatedCart.item_count,
        sections: updatedCart.sections,
        silent: true,
      })
    );
  }

  /**
   * @param {CartLine[]} items
   * @returns {Record<string, number> | null} Quantities keyed by line key, or by variant ID to add the add-on.
   */
  #getUpdates(items) {
    const { variantId, propertyNames } = this.#config;
    const addonLines = items.filter((item) => item.variant_id === variantId);
    const needsAddon = items.some(
      (item) =>
        item.variant_id !== variantId &&
        propertyNames.some((name) => String(item.properties?.[name] ?? '').trim() !== '')
    );

    /** @type {Record<string, number>} */
    const updates = {};

    if (needsAddon && addonLines.length === 0) {
      updates[variantId] = 1;
    } else {
      addonLines.forEach((line, index) => {
        const quantity = needsAddon && index === 0 ? 1 : 0;
        if (line.quantity !== quantity) updates[line.key] = quantity;
      });
    }

    return Object.keys(updates).length > 0 ? updates : null;
  }
}

const configElement = document.getElementById('PersonalisationAddonConfig');

if (configElement?.textContent) {
  try {
    new PersonalisationAddonSync(JSON.parse(configElement.textContent)).start();
  } catch (error) {
    console.error(error);
  }
}
